import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { refreshPricing } from '../../lib/pricing';
import { Check } from 'lucide-react';
import { PricingTab } from '../../components/platformSettings/PricingTab';
import { MessagingTab, FlagsTab } from '../../components/platformSettings/SimpleTabs';
import { MasterAdminsPanel } from '../../components/platformSettings/MasterAdminsPanel';

const SECTIONS = [
  { key: 'pricing',   label: 'Pricing' },
  { key: 'messaging', label: 'Messaging' },
  { key: 'flags',     label: 'Feature flags' },
  { key: 'admins',    label: 'Master admins' },
];

/**
 * Master-admin "Platform settings" page. Owns:
 *   • Load-once fetch from `platform_settings` (single row, id=1).
 *   • A `dirty` diff (JSON.stringify of `original` vs `settings`).
 *   • One `save()` that persists the whole row + calls `refreshPricing()`
 *     so plan cards / signup dropdowns / buy-extra pick up new prices
 *     in the current session without a hard refresh.
 *
 * Each tab is its own component:
 *   • PricingTab (`components/platformSettings/PricingTab.js`)
 *   • MessagingTab + FlagsTab (`.../SimpleTabs.js`)
 *   • MasterAdminsPanel (`.../MasterAdminsPanel.js`) — self-fetching,
 *     doesn't participate in the `dirty` bar.
 */
const SettingsInner = () => {
  const [active, setActive] = useState('pricing');
  const [settings, setSettings] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('platform_settings').select('*').maybeSingle();
      setSettings(data);
      setOriginal(data);
    })();
  }, []);

  const onChange = (patch) => setSettings((prev) => ({ ...prev, ...patch }));
  const dirty = original && settings && JSON.stringify(original) !== JSON.stringify(settings);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        standard_price:            Number(settings.standard_price),
        premium_price:             Number(settings.premium_price),
        annual_discount_pct:       Number(settings.annual_discount_pct),
        extra_barber_price:        Number(settings.extra_barber_price),
        standard_included_barbers: Number(settings.standard_included_barbers),
        premium_included_barbers:  Number(settings.premium_included_barbers),
        standard_features:         Array.isArray(settings.standard_features) ? settings.standard_features : [],
        premium_features:          Array.isArray(settings.premium_features)  ? settings.premium_features  : [],
        sms_cap_premium:           Number(settings.sms_cap_premium),
        sms_cap_standard:          Number(settings.sms_cap_standard ?? 0),
        sms_bundle_200_price:      Number(settings.sms_bundle_200_price ?? 1500),
        sms_bundle_400_price:      Number(settings.sms_bundle_400_price ?? 2800),
        sms_bundle_1000_price:     Number(settings.sms_bundle_1000_price ?? 6000),
        sms_provider:              settings.sms_provider || null,
        absorb_messaging:          !!settings.absorb_messaging,
        signups_open:              !!settings.signups_open,
        signup_trial_days:         settings.signup_trial_days === '' || settings.signup_trial_days == null
                                     ? null
                                     : Number(settings.signup_trial_days),
        updated_at:                new Date().toISOString(),
      };
      // Only include batch-d margin column when the DB actually has it
      // (the initial load returned a non-null value). This lets the
      // page save cleanly BEFORE the migration runs; after the
      // migration, saves include the margin.
      if (original && Object.prototype.hasOwnProperty.call(original, 'sms_margin_per_msg')) {
        payload.sms_margin_per_msg = settings.sms_margin_per_msg == null
          ? 0.02
          : Number(settings.sms_margin_per_msg);
      }
      // Same tolerance for Batch E's STOP toggle.
      if (original && Object.prototype.hasOwnProperty.call(original, 'sms_append_stop_disclosure')) {
        payload.sms_append_stop_disclosure = settings.sms_append_stop_disclosure !== false;
      }
      // Batch F — subscription-gate columns. Both are additive so we
      // only include them once the initial load surfaced them.
      if (original && Object.prototype.hasOwnProperty.call(original, 'enforce_subscription_gate')) {
        payload.enforce_subscription_gate = !!settings.enforce_subscription_gate;
      }
      if (original && Object.prototype.hasOwnProperty.call(original, 'past_due_grace_days')) {
        payload.past_due_grace_days = Number(settings.past_due_grace_days ?? 7);
      }
      const { error: ue } = await supabase.from('platform_settings').update(payload).eq('id', 1);
      if (ue) throw ue;
      setOriginal({ ...settings, ...payload });
      setSavedAt(Date.now());
      // Invalidate the client-side pricing cache so plan cards, signup
      // dropdowns and the buy-extra receipt reflect the new values
      // immediately for the current session.
      await refreshPricing();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <main className="main admin-page">
        <div className="topbar"><div><h2>Settings</h2></div></div>
        <div className="body"><div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div></div>
      </main>
    );
  }

  return (
    <main className="main admin-page">
      <div className="topbar">
        <div>
          <h2>Platform settings</h2>
          <div className="sub">Pricing, caps and feature flags · applied platform-wide</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          {savedAt && !dirty && (
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--green)', fontWeight: 'var(--fw-semibold)' }}>
              <Check size={12} /> Saved
            </span>
          )}
          <button className="btn g" onClick={save} disabled={!dirty || saving} data-testid="settings-save">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="body">
        {error && <div className="modal-error" style={{ marginBottom: 12 }} data-testid="settings-error">{error}</div>}
        <div className="set-grid">
          <div className="set-nav">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                className={`sn ${active === s.key ? 'on' : ''}`}
                onClick={() => setActive(s.key)}
                data-testid={`settings-tab-${s.key}`}
              >{s.label}</button>
            ))}
          </div>

          <div data-testid={`settings-card-${active}`}>
            {active === 'pricing'   && <PricingTab settings={settings} onChange={onChange} />}
            {active === 'messaging' && <MessagingTab settings={settings} onChange={onChange} />}
            {active === 'flags'     && <FlagsTab settings={settings} onChange={onChange} />}
            {active === 'admins'    && (
              <div className="panel set-card">
                <MasterAdminsPanel />
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

const PlatformSettings = () => <AdminShell><SettingsInner /></AdminShell>;
export default PlatformSettings;
